/* eslint-disable no-async-promise-executor */
import { DI } from '@intigral/constant/config/DIContainer';
import { container, ResponseError } from '@intigral/domain';
import { log } from '@intigral/utils';
import { appConfig } from '../config/app.config';
import { errorCodes, screenDetailTypes, STORE_NAME } from '../config/constants';
import { EVENT, KEYS } from '../contexts/MixpanelConstants';
import h, { appData, getReduxGlobal } from '../helpers/common';
import u from '../helpers/url';
import user from '../helpers/user';
import { getGuestRedirectionUrl, isGuestUser, clearWcpClientAuthState } from '../config/guestMode';
import { cookiesNames } from './../config/constants';
import mixpanel from 'mixpanel-browser';
import { COOKIE_NAMES } from '@intigral/constant';
import { datadogRum } from '@datadog/browser-rum';
import { markDataDogSessionForRenew } from '@intigral/utils/lib/datadog';

function displayRefreshTokenError(code) {
    users.handleRefreshTokenAttemps();
    window.dispatchEvent(
        new CustomEvent('REFRESH_TOKEN_ERROR', {
            detail: {
                code: code || 'REFRESH_TOKEN_ERROR',
            },
        })
    );
}

const users = {
    logoutUser: (force_logout = false) => {
        const currentUser = user.getUserInfo();
        const destination = force_logout ? user.getUserForceLogoutUrl() : user.getUserLogoutUrl();

        clearWcpClientAuthState({ skipRedux: true });
        if (appConfig.dataDogRUM.isEnabled) {
            datadogRum.clearUser();
            datadogRum.clearGlobalContext?.();
            datadogRum.stopSession();
            markDataDogSessionForRenew();
        }

        if (isGuestUser(currentUser)) {
            window.location.href = getGuestRedirectionUrl(destination) || destination;
            return;
        }

        window.location.href = destination;
    },

    handleRefreshTokenAttemps() {
        const attemps = this.currentRefreshTokenAttempts(0) + 1;
        h.setStoreItem(STORE_NAME.REFRESH_TOKEN_ATTEMPS, attemps);
        if (attemps > appConfig.refreshTokenMaxAttempts && !isGuestUser(user.getUserInfo())) {
            users.logoutUser();
        }
    },
    currentRefreshTokenAttempts(defaultValue = 0) {
        return Number(h.getStoreItem(STORE_NAME.REFRESH_TOKEN_ATTEMPS)) || defaultValue;
    },

    /**
     * Function to refresh user token.
     * @param reloadOnComplete
     * @param forceRefresh
     * @returns {boolean}
     */
    refreshUserToken: async (reloadOnComplete = false, forceRefresh = false) => {
        const userInfo = user.getUserInfo();
        if (isGuestUser(userInfo)) {
            return false;
        }

        const authService = container.resolve(DI.SERVICES.IAuthService);

        const now = new Date().getTime();
        const duration = userInfo.duration;
        if (h.getStore('refreshApiInProgress')) {
            return true;
        }
        // check if user exceeded the refresh token user attempts
        users.isMaxRefreshTokenAttempt(userInfo.createdTime);
        if (now - userInfo.createdTime > duration - appConfig.refreshTokenOffset || forceRefresh) {
            h.setStore('refreshApiInProgress', true);
            try {
                let userData = await authService.refreshAuthToken(
                    userInfo.username,
                    userInfo.authToken,
                    userInfo.identityToken,
                    userInfo.authSign
                );
                if (!userData.authToken) {
                    throw new Error(
                        'Invalid used data returned while refreshing token. Data:' + JSON.stringify(userData)
                    );
                }
                userData.createdTime = now;
                userData = { ...userInfo, ...userData };
                user.storeUserInfo(userData);
                h.deleteStore('refreshApiInProgress');
                h.deleteStore(STORE_NAME.REFRESH_TOKEN_ATTEMPS);
                if (reloadOnComplete) {
                    window.location.reload();
                }
                window.clearTimeout(users.refreshTimer);
                users.setRefreshTimer(userData);
                return true;
            } catch (e) {
                h.deleteStore('refreshApiInProgress');
                if (e instanceof Error || e instanceof ResponseError) {
                    let failedRefreshToken = appData.failedRefreshToken;
                    const maxAttemptsReached =
                        h.getStore(STORE_NAME.REFRESH_TOKEN_ATTEMPS) > appConfig.refreshTokenMaxAttempts;
                    if (maxAttemptsReached && !isGuestUser(user.getUserInfo())) {
                        users.callTrack('NA', 'Refresh token API Failed - limit exceeded!');
                        users.logoutUser();
                        return false;
                    }
                    h.setContext({ failedRefreshToken: failedRefreshToken + 1 });
                    displayRefreshTokenError(e?.data?.code || e?.data?.status);
                }
            }
        }
        return false;
    },
    setRefreshTimer: (userInfo) => {
        if (!userInfo) {
            userInfo = user.getUserInfo();
        }
        const now = new Date().getTime();
        h.setContext({ failedRefreshToken: 0 });
        const duration = userInfo.duration;
        let nextExec = userInfo.createdTime + duration - appConfig.refreshTokenOffset - now;
        if (nextExec <= 0) {
            nextExec = 300000;
        }

        users.refreshTimer = window.setTimeout(() => {
            users.refreshUserToken();
        }, nextExec);
    },
    /**
     * check if user exceeded the refresh token user attempts force logout
     * @param userCreatedTime
     * @returns {boolean}
     */
    isMaxRefreshTokenAttempt: (userCreatedTime) => {
        if (isGuestUser(user.getUserInfo())) {
            return false;
        }
        const now = new Date().getTime();
        let refreshTokenUserAttempt =
            h.getStore('refreshTokenUserAttempt') === null ? 0 : h.getStore('refreshTokenUserAttempt');
        if (now - userCreatedTime < appConfig.refreshTokenMaxTimer) {
            h.setStore('refreshTokenUserAttempt', refreshTokenUserAttempt + 1);
        } else {
            h.setStore('refreshTokenUserAttempt', 0);
        }
        if (refreshTokenUserAttempt >= appConfig.refreshTokenMaxAttempts && !isGuestUser(user.getUserInfo())) {
            users.callTrack('NA', 'Refresh token limit exceeded!');
            users.logoutUser();
            return false;
        }
        return false;
    },
    /**
     * Adding device.
     * @param config
     * @returns {Promise<apis.jawwyService>}
     */
    addDevice: (config = null, axiosRetryConfig = {}) => {
        return new Promise((resolve, reject) => {
            const added = h.getStore(STORE_NAME.DEVICE);
            if (added) {
                resolve(added);
                return true;
            }
            const deviceService = container.resolve(DI.SERVICES.IDeviceService);
            deviceService
                .addDevice(
                    user.getUsername(),
                    user.getAPIKey() || config?.api?.bolt?.api_key,
                    user.getUserDevice(),
                    user.getUserToken(),
                    user.getIdentityToken(),
                    axiosRetryConfig
                )
                .then((response) => {
                    h.setStore(STORE_NAME.DEVICE, response);
                    resolve(response);

                    users.addDeviceSuccessCallTrack(response.deviceName);
                })
                .catch((error) => {
                    if (error?.data?.code === errorCodes.MAX_DEVICES) {
                        h.setContext({ maxDevicesReached: true });
                        resolve([]);
                    }
                    users.callTrack(
                        error?.data?.code ? error?.data?.code : 'NA',
                        'error occured while adding device!',
                        KEYS.GENERAL.ADD_DEVICE_FAILED
                    );
                    reject(error);
                });
        });
    },
    removeDevice: async (deviceId, isMaxDevicesReached = false) => {
        try {
            let deviceGuid = deviceId ? deviceId : appData.device.deviceGuid;
            const deviceService = container.resolve(DI.SERVICES.IDeviceService);

            const response = await deviceService.deleteDevice(
                user.getUsername(),
                user.getAPIKey(),
                deviceGuid,
                user.getUserToken(),
                user.getIdentityToken()
            );

            if (response === true && isMaxDevicesReached) {
                h.setContext({ maxDevicesReached: false });
                // let userProfileRoute = u?.getDetailsPage(screenDetailTypes.PROFILE_SETTINGS);
                // try to add the current device after remove one of old devices
                try {
                    await users.addDevice();
                    window.location = u.route('');
                    return true;
                } catch (err) {
                    return [];
                }
            }

            return response;
        } catch (error) {
            throw error;
        }
    },
    loginUserWithToken: async ({ t: authToken, u: loginUsername, i: identityToken, s: authSignature }) => {
        if (isGuestUser(user.getUserInfo())) {
            return user.getUserInfo();
        }
        try {
            const authService = container.resolve(DI.SERVICES.IAuthService);
            const userData = await authService.refreshAuthToken(loginUsername, authToken, identityToken, authSignature);
            if (!userData?.username) {
                throw new Error('User object not returned successfully from refresh token API');
            }
            userData.createdTime = Date.now();
            const userStoredData = h.getStore(STORE_NAME.USER) || {};
            h.setStore(STORE_NAME.USER, { ...userStoredData, ...userData });

            if (userData.apiKey) {
                const subscriptionService = container.resolve(DI.SERVICES.ISubscriptionService);
                const subscription = await subscriptionService.getSubscriptionStatus(
                    userData.apiKey,
                    userData.username,
                    userData.authToken,
                    userData.identityToken
                );
                user.updateUserSubscriptionsObject(subscription, false);
                return userData;
            } else {
                throw new Error('Invalid user API key on login');
            }
        } catch (err) {
            const errorCode = err?.data?.code || err?.data?.status;
            displayRefreshTokenError(errorCode);
            return { refreshTokenError: true, code: errorCode || 'REFRESH_TOKEN_ERROR' };
        }
    },
    callTrack: (code, reason, key = EVENT.FORCE_SIGN_OUT) => {
        if (mixpanel.__loaded) {
            mixpanel.track(key, {
                [KEYS.FORCE_SIGN_OUT.FAILURE_CODE]: code,
                [KEYS.FORCE_SIGN_OUT.FAILURE_REASON]: reason,
            });
        } else {
            mixpanel.init(h.getMixPanelKey(), {
                loaded: function (mixpanel) {
                    mixpanel.identify(user.getUid() || user.getAccountId());
                    mixpanel.people.unset(KEYS.PEOPLE.PHONE);
                    mixpanel.track(key, {
                        [KEYS.FORCE_SIGN_OUT.FAILURE_CODE]: code,
                        [KEYS.FORCE_SIGN_OUT.FAILURE_REASON]: reason,
                    });
                },
                persistence: 'localStorage',
            });
        }
    },
    addDeviceSuccessCallTrack: (deviceName) => {
            if (mixpanel.__loaded) {
                mixpanel.track(KEYS.GENERAL.ADD_DEVICE_SUCCESS, {
                    [KEYS.GENERAL.DEVICE_NAME]: deviceName,
                });
            } else {
                mixpanel.init(h.getMixPanelKey(), {
                    loaded: function (mixpanel) {
                        // mixpanel.identify(user.getUsername());
                        // For PDPL use Account ID instead of Username:
                        mixpanel.identify(user.getUid() || user.getAccountId());
                        mixpanel.people.unset(KEYS.PEOPLE.PHONE);
                        mixpanel.track(KEYS.GENERAL.ADD_DEVICE_SUCCESS, {
                            [KEYS.GENERAL.DEVICE_NAME]: deviceName,
                        });
                    },
                    persistence: 'localStorage',
                });
            }
       
    },
};

export default users;
